import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { firstValueFrom, Subscription } from "rxjs";
import { Button } from "primeng/button";
import { Router } from "@angular/router";

import { CustomerCareManagerScheduleService } from "../../../../domains/jobs/services/customer-care-manager-schedule.service";
import { OnlineIndicatorComponent } from "../../../../shared/components/online-indicator/online-indicator.component";
import { JobSkeletonComponent } from "./job-skeleton/job-skeleton.component";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import { NetworkStatusService } from "../../../../shared/services/network-status.service";
import { db, InspectionJob, Lot, SyncableStatus } from "../../../../core/database/db";
import { InspectionJobDetail } from "../../../../domains/jobs/models/InspectionJobDetail";
import SyncManager from "../../../../core/sync/sync-manager";
import { ImageHydrationService } from "../../../../core/sync/image-sync/image-hydration.service";
import { DateUtilsService } from "../../../../shared/services/date.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { AreaConfigurationService } from "../../../../domains/areas/services/area-configuration.service";
import { SupportFooterComponent } from "../../../../shared/components/support-footer/support-footer.component";
import { TagModule } from "primeng/tag";
import { CONSTANTS } from "../../../../constants";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { SyncStatusSheetComponent } from "./job-sync-status-drawer/job-sync-status-drawer.component";
import SyncStatusService, { SyncStatus } from "../../../../core/sync/sync-status-service";
import { BqaJobSyncMetadata, DataSyncMetadataService, SyncMetadata } from "../../../../core/sync/data-sync/data-sync-metadata.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { JobStatus } from "./job-status";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";
import { FeatureService } from "../../../../core/feature-flags/feature.service";
import { AdminConfigKey, LotPrimaryIdentifier } from "../../../../core/feature-flags/models/AdminConfigDto";

export type CcmScheduleJob = InspectionJobDetail & { readyForInspectionDate: Date; hasStarted: boolean };

@Component({
  selector: "app-job-schedule",
  standalone: true,
  imports: [
    CommonModule,
    Button,
    OnlineIndicatorComponent,
    JobSkeletonComponent,
    BackgroundRefreshIndicatorComponent,
    SupportFooterComponent,
    TagModule,
    BottomSheetComponent,
    SyncStatusSheetComponent,
    DataUpdatedFadeDirective
  ],
  templateUrl: "./job-schedule.component.html",
  styleUrls: ["./job-schedule.component.scss"]
})
export class JobScheduleComponent implements OnInit {

  private syncServiceSubscription!: Subscription;
  private onlineSubscription?: Subscription;
  private syncMetadataSubscription?: Subscription;

  constructor(
    protected jobScheduleService: CustomerCareManagerScheduleService,
    private router: Router,
    private networkService: NetworkStatusService,
    private syncManager: SyncManager,
    private mediaHydration: ImageHydrationService,
    private dateUtils: DateUtilsService,
    private logger: LoggerService,
    private areaConfigurationService: AreaConfigurationService,
    private syncStatusService: SyncStatusService,
    private dataSyncMetadataService: DataSyncMetadataService,
    private toast: UiToastService,
    private featureService: FeatureService
  ) {
    this.onlineSubscription = this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
  }

  isOnline: boolean = true;
  formattedDate: string = "";
  inspectionJobs: InspectionJob[] | undefined;
  readyInspections = signal<CcmScheduleJob[]>([]);
  upcomingInspections = signal<CcmScheduleJob[]>([]);
  title: string = "My BQ7 Confirmation Schedule";
  today!: Date;
  dataLoaded: boolean = false;
  isRefreshingInBackground = signal(false);
  dataUpdatedPulse = signal(false);
  lastSyncDate!: Date;
  syncMetadata: Record<string, BqaJobSyncMetadata> = {};
  jobStatusEnum = JobStatus;
  isBottomSheetOpen = false;
  selectedJob: InspectionJob | null = null;
  metadataExists = false;
  lotPrimaryIdentifier: LotPrimaryIdentifier = LotPrimaryIdentifier.LotBlock;

  async ngOnInit(): Promise<void> {
    this.syncMetadata = this.dataSyncMetadataService.getSyncMetadata() || {};
    this.metadataExists = this.syncMetadata !== null;

    this.syncMetadataSubscription = this.dataSyncMetadataService.syncMetadataChanged.subscribe(metadata => {
      this.syncMetadata = metadata;
      this.metadataExists = this.syncMetadata !== null;
    });

    this.today = new Date();

    this.lotPrimaryIdentifier = (await this.featureService.getEffectiveConfig(AdminConfigKey.LotPrimaryIdentifier)) ?? LotPrimaryIdentifier.LotBlock;

    // Render immediately from whatever is already cached locally.
    const cachedJobs = await this.jobScheduleService.getCustomerCareManagerScheduleFromCache();
    const hadCachedData = cachedJobs.length > 0;

    if (hadCachedData) {
      this.inspectionJobs = cachedJobs;
      await this.applySchedule(cachedJobs);
    }

    this.isRefreshingInBackground.set(true);

    // getCustomerCareManagerSchedule() swallows network/sync errors internally and always resolves with
    // whatever's in IndexedDB, so a resolved promise alone doesn't mean anything actually changed. Only
    // scheduleRefreshed firing (emitted strictly before the resolved array is read) means a real sync happened.
    let refreshSucceeded = false;
    const refreshSucceededSub = this.jobScheduleService.scheduleRefreshed.subscribe(() => {
      refreshSucceeded = true;
    });

    const backgroundRefresh = this.loadData().then(() => {
      this.isRefreshingInBackground.set(false);
      if (hadCachedData && refreshSucceeded) {
        this.dataUpdatedPulse.set(true);
      }
    })
      .catch(err => {
        this.logger.error("[Schedule] Failed to refresh schedule from server", err);
        this.isRefreshingInBackground.set(false);
      })
      .finally(() => {
        refreshSucceededSub.unsubscribe();
      });

    // With no cached data to show yet, fall back to the original behavior of
    // waiting on the network fetch before rendering (full skeleton stays up).
    if (!hadCachedData) {
      await backgroundRefresh;
    }
  }

  get isAddressPrimary(): boolean {
    return this.lotPrimaryIdentifier === LotPrimaryIdentifier.Address;
  }

  getPrimaryDisplayValue(item: CcmScheduleJob): string {
    return this.isAddressPrimary ? item.streetAddress : `Lot ${item.lotBlock}`;
  }

  getSecondaryDisplayValue(item: CcmScheduleJob): string {
    return this.isAddressPrimary ? `Lot ${item.lotBlock}` : item.streetAddress;
  }

  async loadData(): Promise<void> {
    this.today = new Date();

    const jobs = await firstValueFrom(
      this.jobScheduleService.getCustomerCareManagerSchedule()
    );
    this.inspectionJobs = jobs;

    this.dataSyncMetadataService.bootstrapSyncMetadata();

    this.prefetchAreaConfigForCaching();

    await this.applySchedule(jobs);
  }

  private async applySchedule(jobs: InspectionJob[]): Promise<void> {
    const lots = await db.lot.where("id").anyOf(jobs.map(i => i.lotId))
      .toArray();

    const lotsById = lots.reduce((prev, current) => {
      prev[current.id] = current; // assign the whole lot
      return prev;
    }, {} as { [key: number]: Lot });

    const ready: CcmScheduleJob[] = [];
    const upcoming: CcmScheduleJob[] = [];

    const jobsForCcm = await Promise.all(
      (jobs ?? [])
        .filter(j => {
          const today = this.dateUtils.normalizeToLocalDate(new Date());

          const isBq7 = j.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ7;
          const isCompleteAndSynced = j.isComplete && j.syncStatus === SyncableStatus.Completed;
          const isRecentlyCompleted = isCompleteAndSynced && this.dateUtils.businessDaysDiff(j.submittedAt ?? new Date(), today) <= 1;
          const shouldShowOnSchedule = isBq7 && (isRecentlyCompleted || isCompleteAndSynced === false);

          return shouldShowOnSchedule;
        })
        .map(async j => {
          const lot = lotsById[j.lotId];
          const hasComplete = await db.assertion
            .where("lotId").equals(j.lotId)
            .and(a => a.isCcmComplete) // this only needs to look at isCcmComplete because this is a CCM only component
            .first();
          let hasStarted = !!hasComplete;
          if (!hasStarted) {
            const hasIssues = await db.assertionIssue
              .where("jobId").equals(j.id)
              .first();
            hasStarted = !!hasIssues;
          }
          return { ...lot, ...j, zones: [], hasStarted } as InspectionJobDetail & { hasStarted: boolean };
        }));

    // Pre-calculate readyForInspectionDate for all jobs
    const jobsWithReadyDate: CcmScheduleJob[] = jobsForCcm.map(job => ({
      ...job,
      readyForInspectionDate: this.dateUtils.calculateReadyForInspectionDate(job.scheduledDate)
    }));

    jobsWithReadyDate.forEach(job => {
      const today = this.dateUtils.normalizeToLocalDate(new Date());

      if (today >= job.readyForInspectionDate) {
        ready.push(job);
      } else {
        const diff = this.dateUtils.businessDaysDiff(today, job.scheduledDate);
        if (diff >= 3 && diff <= 9) {
          upcoming.push(job);
        }
      }
    });

    // Ensure deterministic ordering by scheduled date for UI rendering
    const sortByScheduledDate = (a: InspectionJobDetail, b: InspectionJobDetail) =>
      this.dateUtils.normalizeToLocalDate(a.scheduledDate).getTime() -
      this.dateUtils.normalizeToLocalDate(b.scheduledDate).getTime();

    this.readyInspections.set(ready.sort(sortByScheduledDate));
    this.upcomingInspections.set(upcoming.sort(sortByScheduledDate));

    const dataSyncRecord = await db.syncStatus.orderBy("lastCompletedSync").reverse()
      .first();
    this.lastSyncDate! = dataSyncRecord?.lastCompletedSync ?? new Date(0);

    // Hydrate media for ready jobs only on the schedule page. Lives here (not a one-time call in
    // ngOnInit) so it re-fires after the background refresh replaces the cache-rendered jobs too.
    try {
      const incompleteReadyJobsIds = this.readyInspections().filter(x => x.isComplete === false)
        .map(j => j.id);
      if (incompleteReadyJobsIds.length > 0) {
        await this.mediaHydration.hydrateByJobIds(incompleteReadyJobsIds);
      }
    } catch (err) {
      this.logger.error("[Schedule] Hydration failed", err);
    }

    this.dataLoaded = true;
  }

  status(job: InspectionJob): JobStatus {

    const jobIsFlaggedAsComplete = job.isComplete;
    if (jobIsFlaggedAsComplete) {

      const metadata = this.syncMetadata[job.id] || {};

      const isOffline = this.isOnline === false;
      const isSyncIncomplete = this.syncStatusService.getSyncStatus() === SyncStatus.Incomplete;
      const isUploadComplete = Object.values(metadata).length > 0 && Object.values(metadata)
        .filter(entry => typeof entry === "object")
        .reduce<boolean>((prev: boolean, current: SyncMetadata) => {
          const { localTotalCount, remoteCount } = this.dataSyncMetadataService.getEffectiveSyncMetadata(current);
          return prev && remoteCount >= localTotalCount;
        }, true);

      if (isUploadComplete) {
        return JobStatus.CompleteUploaded;
      } else if (isOffline) {
        return JobStatus.CompleteOffline;
      } else if (isSyncIncomplete) {
        return JobStatus.CompletePending;
      }
      return JobStatus.CompleteInProgress;
    }

    const diff = this.dateUtils.businessDaysDiff(
      this.dateUtils.normalizeToLocalDate(new Date()),
      this.dateUtils.normalizeToLocalDate(job.scheduledDate)
    );

    if (diff < 0) {
      return JobStatus.Overdue;
    };

    if (diff <= 2) {
      return JobStatus.Ready;
    };

    return JobStatus.Upcoming;
  }

  trackById(_idx: number, job: { id: number }) {
    return job.id;
  }

  startInspection(item: InspectionJob) {
    this.router.navigateByUrl(`/ccm/bq7/job/${item.id}`);
  }

  viewReport(item: { id: number }) {
    this.router.navigateByUrl(`/ccm/bq7/job/${item.id}/report`, {
      state: {
        online: this.isOnline
      }
    });
  }

  ngOnDestroy() {
    if (this.syncServiceSubscription) {
      this.syncServiceSubscription.unsubscribe();
    }
    this.onlineSubscription?.unsubscribe();
    this.syncMetadataSubscription?.unsubscribe();
  }

  viewJobSyncStatus(item: InspectionJob) {
    this.selectedJob = item;
    const selectedJobMetadata = this.syncMetadata[item.id];
    if (selectedJobMetadata) {
      this.isBottomSheetOpen = true;
    } else {
      this.toast.warn("No sync information available for this job yet. Please try again in a few moments.");
      this.dataSyncMetadataService.updateSyncMetadata();
    }
  }

  onSyncSheetClose() {
    this.isBottomSheetOpen = false;
    this.selectedJob = null;
  }

  getOverdueJobRoute(job: InspectionJob): string {
    return `/ccm/bq7/job/${job.id}`;
  }

  private prefetchAreaConfigForCaching() {
    if (this.inspectionJobs) {
      const divisionIds = this.inspectionJobs.map(job => job.divisionId);
      const uniqueDivisionIds = Array.from(new Set(divisionIds));
      for (const divisionId of uniqueDivisionIds) {
        this.areaConfigurationService.getConfiguration(divisionId).subscribe(_ => { });
      }
    }
  }
}

